import { useCallback, useState, useLayoutEffect } from 'react';

export default function useSessionStorage( key, initialValue ) {
    if ( !key ) {
        throw new Error( 'useSessionStorage key may not be falsy' );
    }

    const initializer = function ( key ) {
        try {
            const sessionStorageValue = sessionStorage.getItem( key );
            if ( sessionStorageValue !== null ) {
                return JSON.parse( sessionStorageValue );
            } else {
                initialValue && sessionStorage.setItem( key, JSON.stringify( initialValue ) );
                return initialValue;
            }
        } catch {
            // prevent error if the user is in private mode or has storage restrictions -
            // localStorage can throw in this case
            return initialValue;
        }
    };

    const [ state, setState ] = useState( () => initializer( key ) );

    useLayoutEffect( () => setState( initializer( key ) ), [ key ] );

    const set = useCallback(
        ( value ) => {
            try {
                const newState = value;
                if ( typeof newState === 'undefined' ) {
                    return;
                }
                let val = JSON.stringify( newState );

                sessionStorage.setItem( key, val );
                setState( JSON.parse( val ) );
            } catch {
                // prevent error if the user is in private mode or has storage restrictions -
                // localStorage can throw in this case
            }
        },
        [ key, setState ]
    );

    const remove = useCallback( () => {
        try {
            sessionStorage.removeItem( key );
            setState( undefined );
        } catch {
            // prevent error if the user is in private mode or has storage restrictions -
            // localStorage can throw in this case
        }
    }, [ key, setState ] );

    return [ state, set, remove ];
}